import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import classnames from 'classnames';
import { color } from '../../../app/theme';
import { ModalType } from '../../../app/types';
import { openModal } from '../../../features/modalSlice';
import Template from './Template';
import TemplateDropdown from './TemplateDropdown';
import IconEdit from '../../../assets/images/Icons/IconEdit';
import IcTagSvg from '../../../assets/images/svg/ic-tag.svg';
import IcLinkSvg from '../../../assets/images/svg/ic-link.svg';
import Icons from './Icons';
import Checkbox from '../Checkbox';
import TextFieldCardCounter from '../TextFieldCardCounter';
import Tooltip from '../Tooltip';
import { validateHttpsAndHttpPath, validateNo8Path } from '../../../app/utils';
import icHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';

const useStyles = makeStyles(
  () => ({
    root: {
      display: 'flex',
      alignItems: 'center',
    },
    dropdown: {
      flex: '0 0 auto',
      width: '40%',
    },
    contentContainer: {
      position: 'relative',
      width: '100%',
      margin: '0 0 0 8px',
    },
    content: {
      cursor: 'pointer',
      width: '100%',
      height: 44,
      fontSize: 15,
      color: color.$3,
      lineHeight: 1,
      borderRadius: 24,
      border: 'solid 1px #bdbdbd',
      backgroundColor: '#ffffff',
      padding: '15px 48px 14px 24px',
      resize: 'none',
      outline: 'none',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      '&::placeholder': {
        fontSize: 15,
        color: color.$3,
      },
    },
    icon: {
      cursor: 'pointer',
      position: 'absolute',
      top: 10,
      right: 16,
    },
    linkUrlBlock: {
      marginTop: 12,
    },
    checkbox: {
      marginLeft: 0,
    },
    'checkbox-label': {
      fontSize: 15,
      lineHeight: 1,
      color: color.$13,
    },

    tagContainer: {
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        display: 'block',
        width: 24,
        Height: 24,
        margin: '0 6px 0 0',
      },
    },
    textField: {
      display: 'block',
      flex: '1 1 auto',
      width: '100%',
      height: 44,
      fontSize: 15,
      lineHeight: '21px',
      outline: 'none',
      padding: '0 16px',
      borderRadius: 22,
      border: `1px solid ${color.$10}`,
      backgroundColor: color.$7,
      '&:focus': {
        borderColor: color.$1,
      },
    },
    'textField--error': {
      border: `1px solid ${color.$17} !important`,
    },
    linkUrlSetting: {
      width: 368,
      marginTop: 12,
    },
    linkUrlData: {
      marginTop: 12,
      display: 'flex',
      alignItems: 'center',
      '& > div': {
        width: '50%',
      },
      '& > div:last-child': {
        marginLeft: 8,
      },
    },
    tootip: {
      display: 'flex',
      alignItems: 'center',
    },
  }),
  {
    name: 'RichVideoTemplate',
  },
);

function RichVideoTemplate({
  template,
  onChange,
  onChangeType,
  onDelete,
  platformType,
  onUp,
  onDown,
  messageLength,
  disabled,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const { t } = useTranslation();
  const element = (template?.data?.elements || [])[0];
  const { fileName, videoUrl, buttons } = element || {};
  const button = (buttons || [])[0];
  const { title: linkUrlTitle, data: linkUrl, tags } = button || {};
  const [hasLinkUrl, setHasLinkUrl] = useState(false);
  const [isModifiedUrl, setIsModifiedUrl] = useState(false);

  function handleOpenUploadFileDialog() {
    if (!disabled) {
      dispatch(
        openModal({
          modalType: ModalType.UPLOAD_DIALOG,
          modalProps: {
            orgId,
            name: fileName || '',
            url: videoUrl,
            platform: platformType,
            isVideo: true,
            onUploaded: data => {
              onChange(
                {
                  ...template,
                  data: {
                    elements: [
                      {
                        ...element,
                        fileName: data?.data?.name,
                        videoUrl: data?.data?.url,
                        previewImageUrl: data?.data?.previewUrl,
                        aspectRatio: data?.data?.aspectRatio,
                      },
                    ],
                  },
                },
                template.index,
              );
            },
          },
        }),
      );
    }
  }

  const handleCheckboxChange = () => {
    if (hasLinkUrl) {
      onChange(
        {
          ...template,
          data: {
            elements: [
              {
                ...element,
                buttons: [
                  {
                    type: 'url',
                    title: '',
                    data: '',
                    tags: [],
                  },
                ],
              },
            ],
          },
        },
        template.index,
      );
    }
    setHasLinkUrl(!hasLinkUrl);
  };

  const handleChangeTitle = value => {
    onChange(
      {
        ...template,
        data: {
          elements: [
            {
              ...element,
              buttons: [
                {
                  ...button,
                  title: value,
                },
              ],
            },
          ],
        },
      },
      template.index,
    );
  };

  const handleChangeData = e => {
    const v = e.target.value;

    onChange(
      {
        ...template,
        data: {
          elements: [
            {
              ...element,
              buttons: [
                {
                  ...button,
                  data: v,
                },
              ],
            },
          ],
        },
      },
      template.index,
    );
    setIsModifiedUrl(true);
  };

  const handleChangeTag = e => {
    const v = e.target.value;

    onChange(
      {
        ...template,
        data: {
          elements: [
            {
              ...element,
              buttons: [
                {
                  ...button,
                  tags: [v],
                },
              ],
            },
          ],
        },
      },
      template.index,
    );
  };

  useEffect(() => {
    if (linkUrl) {
      setHasLinkUrl(true);
    }
  }, [linkUrl]);

  return (
    <Template>
      <div className={classes.root}>
        <TemplateDropdown
          className={classes.dropdown}
          platformType={platformType}
          messageTemplatetype={
            template.templateType === 'richvideo' ||
            template.data.templateType === 'richvideo'
              ? 'richvideo'
              : ''
          }
          onChange={onChangeType}
          disabled={disabled}
        />
        <div
          className={classes.contentContainer}
          onClick={handleOpenUploadFileDialog}
        >
          <input
            className={classes.content}
            value={videoUrl !== '' ? fileName : ''}
            placeholder={t('templates.placeholder-file')}
            readOnly
          />
          <div className={classes.icon}>
            <IconEdit
              className={classes.iconSvg}
              id={`edit-${template.data.index}`}
              color={color.$10}
              hoverColor={color.$10}
            />
          </div>
        </div>
        <Icons
          index={template.index}
          onUp={onUp}
          onDown={onDown}
          onDelete={onDelete}
          messageLength={messageLength}
          disabled={disabled}
        />
      </div>
      <div className={classes.linkUrlBlock}>
        <div>
          <FormControlLabel
            className={classes.checkbox}
            classes={{
              label: classes['checkbox-label'],
            }}
            control={<Checkbox checked={hasLinkUrl} disabled={disabled} />}
            label={
              <div className={classes.tootip}>
                {t('templates.richvideo-edit.linkUrl')}
                <Tooltip
                  light
                  placement="top"
                  title={t('templates.richvideo-edit.linkUrl-tooltip')}
                >
                  <img
                    style={{ marginLeft: 6 }}
                    src={icHelpCircle}
                    alt="alert"
                  />
                </Tooltip>
              </div>
            }
            onChange={handleCheckboxChange}
          />
        </div>
        {hasLinkUrl && (
          <div className={classes.linkUrlSetting}>
            <TextFieldCardCounter
              placeholder={t('templates.button-placeholder')}
              maxCount={20}
              value={linkUrlTitle}
              onChange={handleChangeTitle}
              isDisable={disabled}
            />
            <div className={classes.linkUrlData}>
              <div className={classes.tagContainer}>
                <img src={IcLinkSvg} alt="Link" />
                <input
                  className={classnames(classes.textField, {
                    [classes['textField--error']]:
                      isModifiedUrl &&
                      (linkUrl === '' ||
                        (!validateHttpsAndHttpPath(linkUrl) &&
                          !validateNo8Path(linkUrl))),
                  })}
                  value={linkUrl}
                  placeholder={t('templates.url-placeholder')}
                  onChange={handleChangeData}
                  disabled={disabled}
                />
              </div>
              <div className={classes.tagContainer}>
                <img src={IcTagSvg} alt="Tag" />
                <input
                  type="text"
                  className={classes.textField}
                  placeholder={t('templates.tags-platform')}
                  value={tags[0] || ''}
                  onChange={handleChangeTag}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Template>
  );
}

RichVideoTemplate.defaultProps = {
  onChange: () => {},
  onChangeType: () => {},
  onDelete: () => {},
  onUp: () => {},
  onDown: () => {},
  messageLength: 0,
};

RichVideoTemplate.propTypes = {
  template: PropTypes.shape({
    contentType: PropTypes.oneOf([
      'application/x-image',
      'application/x-audio',
      'application/x-video',
    ]),
    data: PropTypes.shape({
      content: PropTypes.string,
    }),
  }).isRequired,
  onChange: PropTypes.func,
  onChangeType: PropTypes.func,
  onDelete: PropTypes.func,
  onUp: PropTypes.func,
  onDown: PropTypes.func,
  messageLength: PropTypes.number,
};

export default RichVideoTemplate;
